import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import { StyledButton } from '../styles/StyledButtons';
import { Palette } from 'Palette';

interface ApprovalUIProps {
  correctChoice: string;
  deviceInfo?: {
    platform?: string;
    deviceModel?: string;
    osVersion?: string;
    deviceName?: string;
    userAgent?: string;
  };
  onApprove: () => void;
  onDeny: () => void;
}

export function ApprovalUI({ correctChoice, deviceInfo, onApprove, onDeny }: ApprovalUIProps) {
  const isApproved = !!correctChoice;

  // Format device information for display (user-friendly)
  const formatDeviceInfo = () => {
    if (!deviceInfo) return 'Unknown device';

    // Clean and simplify the device name/model
    const cleanDeviceName = (name: string) => {
      if (!name) return '';

      // If it's a very long technical string, use aggressive fallback
      if (name.length > 50 || name.includes('/') || name.includes(':')) {
        // Try to extract meaningful parts from technical strings
        if (name.toLowerCase().includes('samsung')) return 'Samsung Device';
        if (name.toLowerCase().includes('galaxy')) return 'Galaxy Device';
        if (name.toLowerCase().includes('iphone')) return 'iPhone';
        if (name.toLowerCase().includes('pixel')) return 'Pixel Device';

        // Ultimate fallback for unrecognized technical strings
        return 'Mobile Device';
      }

      // Remove technical build info in parentheses
      return name.split('(')[0].trim();
    };

    const cleanOSVersion = (version: string) => {
      if (!version) return '';

      // Extract just the main version (e.g., "Android 14" from "Android 14.0.0")
      const match = version.match(/(iOS|Android)\s*(\d+)/i);
      return match ? `${match[1]} ${match[2]}` : version.split(' ').slice(0, 2).join(' ');
    };

    // Priority: Custom device name > Model name > Platform
    let displayName = '';

    if (deviceInfo.deviceName && deviceInfo.deviceName !== deviceInfo.deviceModel) {
      // Use custom name if different from model (e.g., "Steve's S22 Ultra")
      displayName = cleanDeviceName(deviceInfo.deviceName);
    } else if (deviceInfo.deviceModel) {
      // Use model name (e.g., "Galaxy S22 Ultra", "iPhone 15 Pro")
      displayName = cleanDeviceName(deviceInfo.deviceModel);
    } else if (deviceInfo.platform) {
      // Fallback to platform
      displayName =
        deviceInfo.platform === 'iOS'
          ? 'iPhone'
          : deviceInfo.platform === 'Android'
            ? 'Android Device'
            : 'Mobile Device';
    }

    // If we still have a technical string, use final fallback
    if (!displayName || displayName.length > 30 || displayName.includes('/')) {
      displayName = deviceInfo.platform === 'iOS' ? 'iPhone' : 'Android Device';
    }

    // Add simplified OS version if available
    if (deviceInfo.osVersion) {
      const cleanOS = cleanOSVersion(deviceInfo.osVersion);
      if (cleanOS) {
        return `${displayName} (${cleanOS})`;
      }
    }

    return displayName || 'Mobile Device';
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
      <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />

      {!isApproved ? (
        <>
          <Typography variant="h6" gutterBottom>
            Mobile Device Detected!
          </Typography>

          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: 'rgba(196, 212, 189, 0.1)',
              borderRadius: 1,
              border: '1px solid rgba(196, 212, 189, 0.3)',
              maxWidth: '400px',
              width: '100%',
              mx: 'auto',
              wordBreak: 'break-word',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'center' }}>
              <PhoneIcon sx={{ mr: 1, color: Palette.default.buttonBg, fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Device requesting access:
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
                color: Palette.default.buttonBg,
                mb: 0.5,
                textAlign: 'center',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto',
              }}
            >
              {formatDeviceInfo()}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
                display: 'block',
                textAlign: 'center',
              }}
            >
              Verify this is your device before approving
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
            Your mobile device has scanned the QR code. Verify this is your device and click approve
            to continue with login.
          </Typography>

          <StyledButton variant="contained" size="large" onClick={onApprove} noTopSpacing={true}>
            Approve Login
          </StyledButton>

          <StyledButton
            variant="outlined"
            buttonVariant="inactiveOrCancel"
            onClick={onDeny}
            noTopSpacing={true}
          >
            Deny
          </StyledButton>
        </>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Login approved! Select this number on your mobile device:
          </Typography>

          <Box
            sx={{
              textAlign: 'center',
              mb: 2,
              p: 3,
              backgroundColor: 'rgba(196, 212, 189, 0.3)', // tone1 background variant
              borderRadius: 2,
              border: `2px solid ${Palette.default.buttonBg}`, // consistent with app colors
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'Inter, sans-serif', // consistent with app font
                letterSpacing: 4,
                color: Palette.default.buttonBg, // use app primary color
                fontWeight: 'bold',
              }}
            >
              {correctChoice}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Select this number from the 3 options on your mobile device
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
}
